import React, { useEffect, useRef, useState } from "react";
import LudDashboardModal from "../../modals/LudDashboardModal";
import { Button, Input, NoticeBox, Tags } from "../../ui";
import BranchPicker from "./BranchPicker";
import { createChangeRequestAPI, editChangeRequestAPI, getApplicationDetailAPI } from "../../../utils/api/applications";
import WizardSteps from "./WizardSteps";
import { errorMessage } from "./applicationsText";
import { mapServerFields, validateAccount, validateBranches, validateManager } from "./applicationsModel";

const emptyValues = { account: { id: "", email: "", password: "" }, manager: { name: "", phone: "", email: "" }, branches: {} };

// Integration wizard: 1 account, 2 account manager, 3 branches (each with its
// own application id and name), 4 review. Values survive Back/Next. Submitting
// creates (or, when `request` is given, edits) an integration REQUEST with
// status "in progress"; nothing live changes before an administrator approves.
export default function IntegrateModal({ open, storeId, app, appName, t, token, request = null, initialBranchIds = [], startStep = 0, onClose, onDone }) {
  const [step, setStep] = useState(0);
  const [detail, setDetail] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const initialKey = initialBranchIds.join(",");
  useEffect(() => {
    if (!open || !app) return undefined;
    let current = true;
    setLoading(true);
    setStep(startStep);
    setErrors({});
    setFormError("");
    getApplicationDetailAPI(token, storeId, app.key)
      .then((res) => {
        if (!current) return;
        const integration = res.data.integration;
        setDetail(integration);
        const proposedBy = new Map((request?.proposed?.branches || []).map((item) => [item.branchId, item]));
        const liveAccount = integration.account;
        const manager = request?.proposed?.manager || integration.manager;
        const account = request?.proposed?.account || liveAccount;
        setValues({
          account: { id: account?.id || "", email: account?.email || "", password: "" },
          manager: { name: manager?.name || "", phone: manager?.phone || "", email: manager?.email || "" },
          branches: Object.fromEntries(
            integration.branches.map((branch) => {
              const proposed = proposedBy.get(branch.id);
              return [
                branch.id,
                request
                  ? { selected: Boolean(proposed), appId: proposed?.appId ?? branch.appId ?? "", appName: proposed?.appName ?? branch.appName ?? "" }
                  : {
                      // Opened from a branch: that branch only. Otherwise mapped branches (to edit)
                      // and freshly registered ones start selected.
                      selected: initialBranchIds.length ? initialBranchIds.includes(branch.id) : branch.integration !== "unconfigured" || branch.subscription?.status === "completed",
                      appId: branch.appId || "",
                      appName: branch.appName || "",
                    },
              ];
            }),
          ),
        });
      })
      .catch((e) => current && setFormError(errorMessage(t, e)))
      .finally(() => current && setLoading(false));
    return () => {
      current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, app, storeId, token, t, request, initialKey, startStep]);

  if (!open || !app) return null;

  const branches = detail?.branches || [];
  const hasPassword = Boolean(detail?.account?.hasPassword || request?.proposed?.account?.passwordChanged);
  const blocked = (branch) => {
    if (branch.subscription && branch.subscription.status !== "completed") return t.integrate.waitSubscription;
    if (branch.pendingRequest && branch.pendingRequest.id !== request?.id) return t.flow.hasPending;
    return "";
  };
  const fieldError = (path) => (errors[path] ? t.integrate[errors[path]] || t.integrate.invalid : undefined);
  const selectedBranches = branches.filter((branch) => values.branches[branch.id]?.selected && !blocked(branch));
  // Only branches the user can actually pick take part in validation.
  const effectiveBranches = Object.fromEntries(branches.map((branch) => [branch.id, { ...values.branches[branch.id], selected: Boolean(values.branches[branch.id]?.selected) && !blocked(branch) }]));

  const setAccount = (key) => (event) => setValues((v) => ({ ...v, account: { ...v.account, [key]: event.target.value } }));
  const setManager = (key) => (event) => setValues((v) => ({ ...v, manager: { ...v.manager, [key]: event.target.value } }));
  const setBranch = (id, patch) => setValues((v) => ({ ...v, branches: { ...v.branches, [id]: { ...v.branches[id], ...patch } } }));

  const stepErrors = (index) => (index === 0 ? validateAccount(values.account, { hasPassword }) : index === 1 ? validateManager(values.manager) : index === 2 ? validateBranches(effectiveBranches) : {});

  const next = () => {
    const found = stepErrors(step);
    setErrors(found);
    if (Object.keys(found).length) {
      setFormError(t.errors.VALIDATION_FAILED);
      return;
    }
    setFormError("");
    setStep((current) => current + 1);
  };
  const back = () => {
    setFormError("");
    setStep((current) => Math.max(0, current - 1));
  };

  const submit = async () => {
    const found = { ...stepErrors(0), ...stepErrors(1), ...stepErrors(2) };
    if (Object.keys(found).length) {
      setErrors(found);
      setFormError(t.errors.VALIDATION_FAILED);
      setStep(Object.keys(found).some((path) => path.startsWith("account")) ? 0 : Object.keys(found).some((path) => path.startsWith("manager")) ? 1 : 2);
      return;
    }
    setBusy(true);
    setFormError("");
    const payload = {
      account: values.account,
      manager: values.manager,
      branches: selectedBranches.map((branch) => ({ branchId: branch.id, appId: values.branches[branch.id].appId, appName: values.branches[branch.id].appName })),
    };
    try {
      if (request) await editChangeRequestAPI(token, storeId, request.id, { version: request.version, ...payload });
      else await createChangeRequestAPI(token, storeId, app.key, { type: "integration", ...payload });
      onDone?.(request ? t.flow.requestUpdated : t.flow.requestSent, "success");
    } catch (e) {
      if (!alive.current) return;
      const mapped = mapServerFields(e.data?.fields, selectedBranches.map((branch) => branch.id));
      setErrors(mapped);
      if (Object.keys(mapped).length) setStep(Object.keys(mapped).some((path) => path.startsWith("account")) ? 0 : Object.keys(mapped).some((path) => path.startsWith("manager")) ? 1 : 2);
      setFormError(errorMessage(t, e));
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const last = step === 3;
  const footer = (
    <>
      <Button variant="neutral" appearance="soft" onClick={step === 0 ? onClose : back} disabled={busy}>
        {step === 0 ? t.cancel : t.back}
      </Button>
      {last ? (
        <Button variant="primary" appearance="filled" onClick={submit} disabled={busy || loading} loading={busy}>
          {request ? t.flow.saveChanges : t.flow.submitRequest}
        </Button>
      ) : (
        <Button variant="primary" appearance="filled" onClick={next} disabled={busy || loading}>
          {t.next}
        </Button>
      )}
    </>
  );

  const title = request ? t.flow.editingRequest(request.reference) : detail?.account ? t.flow.editTitle(appName) : t.integrate.title(appName);

  return (
    <LudDashboardModal open title={title} size="lg" onClose={onClose} busy={busy} footer={footer}>
      <WizardSteps steps={t.flow.integrateSteps} index={step} />
      {loading ? (
        <p className="lud-app-lead" role="status">{t.loading}</p>
      ) : (
        <form className="lud-app-form" onSubmit={(event) => { event.preventDefault(); if (last) submit(); else next(); }} noValidate>
          {formError && <NoticeBox variant="danger" size="sm">{formError}</NoticeBox>}

          {step === 0 && (
            <fieldset className="lud-app-fieldset" disabled={busy}>
              <legend>{t.integrate.account}</legend>
              <div className="lud-app-form-grid">
                <Input label={t.integrate.accountId} value={values.account.id} onChange={setAccount("id")} error={fieldError("account.id")} required autoComplete="off" />
                <Input label={t.integrate.accountEmail} type="email" dir="ltr" value={values.account.email} onChange={setAccount("email")} error={fieldError("account.email")} required autoComplete="off" />
                <Input
                  label={t.integrate.accountPassword}
                  type="password"
                  dir="ltr"
                  value={values.account.password}
                  onChange={setAccount("password")}
                  error={fieldError("account.password")}
                  helperText={hasPassword ? t.integrate.passwordKeep : undefined}
                  required={!hasPassword}
                  autoComplete="new-password"
                />
              </div>
            </fieldset>
          )}

          {step === 1 && (
            <fieldset className="lud-app-fieldset" disabled={busy}>
              <legend>{t.integrate.manager}</legend>
              <div className="lud-app-form-grid">
                <Input label={t.integrate.managerName} value={values.manager.name} onChange={setManager("name")} error={fieldError("manager.name")} required />
                <Input label={t.integrate.managerPhone} type="tel" dir="ltr" value={values.manager.phone} onChange={setManager("phone")} error={fieldError("manager.phone")} required />
                <Input label={t.integrate.managerEmail} type="email" dir="ltr" value={values.manager.email} onChange={setManager("email")} error={fieldError("manager.email")} required />
              </div>
            </fieldset>
          )}

          {step === 2 && (
            <fieldset className="lud-app-fieldset" disabled={busy}>
              <legend>{t.integrate.branches}</legend>
              <p className="lud-app-hint">{t.integrate.branchesHint}</p>
              {errors.branches && <small className="lud-app-error" role="alert">{t.integrate.noBranches}</small>}
              <BranchPicker
                branches={branches}
                selected={branches.filter((branch) => values.branches[branch.id]?.selected).map((branch) => branch.id)}
                onToggle={(id) => setBranch(id, { selected: !values.branches[id]?.selected })}
                reasonFor={blocked}
                originId={initialBranchIds[0] || null}
                t={t}
                disabled={busy}
                renderExtra={(branch) => {
                  const row = values.branches[branch.id] || { appId: "", appName: "" };
                  return (
                    <div className="lud-app-form-grid lud-app-branch-fields">
                      <Input label={t.integrate.appId} dir="ltr" value={row.appId} onChange={(event) => setBranch(branch.id, { appId: event.target.value })} error={fieldError(`branches.${branch.id}.appId`)} required />
                      <Input label={t.integrate.appName} value={row.appName} onChange={(event) => setBranch(branch.id, { appName: event.target.value })} error={fieldError(`branches.${branch.id}.appName`)} required />
                    </div>
                  );
                }}
              />
            </fieldset>
          )}

          {step === 3 && (
            <div className="lud-app-stack">
              <NoticeBox variant="info" size="sm">{t.flow.reviewHint}</NoticeBox>
              <dl className="lud-app-kv">
                <dt>{t.integrate.accountId}</dt>
                <dd dir="ltr">{values.account.id}</dd>
                <dt>{t.integrate.accountEmail}</dt>
                <dd dir="ltr">{values.account.email}</dd>
                <dt>{t.integrate.accountPassword}</dt>
                <dd dir="ltr">{values.account.password ? `${t.flow.passwordMasked} (${t.flow.passwordNew})` : t.flow.passwordUnchanged}</dd>
                <dt>{t.integrate.managerName}</dt>
                <dd>{values.manager.name}</dd>
                <dt>{t.integrate.managerPhone}</dt>
                <dd dir="ltr">{values.manager.phone}</dd>
                <dt>{t.integrate.managerEmail}</dt>
                <dd dir="ltr">{values.manager.email}</dd>
                <dt>{t.integrate.integrationStatus}</dt>
                <dd><Tags variant="warning" appearance="soft" size="sm">{t.status.integrationInProgress}</Tags></dd>
              </dl>
              <strong>{t.flow.selectedBranches}</strong>
              <div className="lud-app-table-wrap">
                <table className="lud-app-table">
                  <thead>
                    <tr><th>{t.manage.branches}</th><th>{t.integrate.appId}</th><th>{t.integrate.appName}</th></tr>
                  </thead>
                  <tbody>
                    {selectedBranches.map((branch) => (
                      <tr key={branch.id}>
                        <td>{branch.name}</td>
                        <td dir="ltr">{values.branches[branch.id].appId}</td>
                        <td>{values.branches[branch.id].appName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </form>
      )}
    </LudDashboardModal>
  );
}
